﻿import { bootstrapSheet } from '../../core/styles/bootstrap.js';
import { eumBaseSheet } from '../../core/styles/eum-base.js';
import { i18n } from '../../core/i18n/i18n-service.js';
// @ts-ignore — handled by esbuild inline-css plugin
import componentSheet from './eum-pagination.css';

/**
 * <eum-pagination> — page navigation using Bootstrap pagination component.
 * Driven via attributes: total-count, page-size, current-page.
 * Fires `eum-page-change` CustomEvent with { page: number }.
 */
export class EumPagination extends HTMLElement {
    private nav!: HTMLElement;
    private readonly onNavClick = (e: Event): void => {
        const btn = (e.target as HTMLElement).closest<HTMLButtonElement>('button[data-page]');
        if (!btn || btn.closest('.disabled')) return;

        const page = parseInt(btn.dataset.page!, 10);
        const total = this.totalPages;

        if (page >= 1 && page <= total) {
            this.dispatchEvent(
                new CustomEvent('eum-page-change', {
                    bubbles: true,
                    composed: true,
                    detail: { page },
                })
            );
        }
    };

    static get observedAttributes(): string[] {
        return ['total-count', 'page-size', 'current-page']; 
    }

    connectedCallback(): void {
        const shadow = this.attachShadow({ mode: 'open' });
        shadow.adoptedStyleSheets = [bootstrapSheet, eumBaseSheet, componentSheet];   

        shadow.innerHTML = /*html*/ `<nav aria-label="${this.escapeAttr(i18n.t('pagination.navLabel'))}"><ul class="pagination justify-content-center mb-0"></ul></nav>`;

        this.nav = shadow.querySelector('ul')!;
        this.nav.addEventListener('click', this.onNavClick);
        this.render();
    }

    disconnectedCallback(): void {
        if (this.nav) {
            this.nav.removeEventListener('click', this.onNavClick);
        }
    }

    attributeChangedCallback(): void {
        if (this.nav) this.render(); 
    }

    private get totalCount(): number {
        return parseInt(this.getAttribute('total-count') || '0', 10);
    }

    private get pageSize(): number {
        return parseInt(this.getAttribute('page-size') || '25', 10);
    }

    private get currentPage(): number {
        return parseInt(this.getAttribute('current-page') || '1', 10);
    }

    private get totalPages(): number {
        return Math.max(1, Math.ceil(this.totalCount / this.pageSize));
    }

    private render(): void {
        const total = this.totalPages;
        const current = this.currentPage;

        if (total <= 1) {
            this.nav.innerHTML = '';
            return;
        }

        const pages = this.getPageRange(current, total);

        let html = `
            <li class="page-item${current <= 1 ? ' disabled' : ''}">
                <button class="page-link" data-page="${current - 1}" aria-label="${this.escapeAttr(i18n.t('pagination.previous'))}">&laquo;</button>
            </li>`;

        for (const p of pages) {
            if (p === '...') {
                html += `<li class="page-item disabled"><span class="page-link">&hellip;</span></li>`;
            } else {
                html += `<li class="page-item${p === current ? ' active' : ''}"><button class="page-link" data-page="${p}">${p}</button></li>`;
            }
        }

        html += `
            <li class="page-item${current >= total ? ' disabled' : ''}">
                <button class="page-link" data-page="${current + 1}" aria-label="${this.escapeAttr(i18n.t('pagination.next'))}">&raquo;</button>
            </li>`;

        this.nav.innerHTML = html;
    }

    private getPageRange(current: number, total: number): (number | '...')[] {
        const delta = 2;
        const range: (number | '...')[] = [];
        const left = Math.max(2, current - delta);
        const right = Math.min(total - 1, current + delta);

        range.push(1);
        if (left > 2) range.push('...');

        for (let i = left; i <= right; i++) {
            range.push(i);
        }

        if (right < total - 1) range.push('...');
        if (total > 1) range.push(total);

        return range;
    }

    private escapeAttr(str: string): string {
        return str.replace(/&/g, '&amp;').replace(/"/g, '&quot;');
    }

}

customElements.define('eum-pagination', EumPagination);